/**
 * Shared follow-ups UI (school profile + risk triage).
 * All operations (create, take-action, add-review) persist via the backend API.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Input, Modal, Select, message } from 'antd';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { AuthUser } from '@/features/auth/types/auth.types';
import type { ComplianceCreateScope } from '@/features/reports/api/compliance.dto';
import {
  ComplianceActivityType,
  ComplianceFollowUpSource,
  ComplianceFollowUpStatus,
  type ComplianceFollowUpDto,
} from '@/features/reports/api/compliance.dto';
import { complianceApi } from '@/features/reports/api/compliance.api';
import { ApiError } from '@/shared/api';
import { Button } from '@/shared/components/Button';

export const ADD_COMPLIANCE_ISSUE_LABEL = 'Add Compliance issue';

export type FollowUpStatus = 'Open' | 'In review' | 'Resolved';

export interface FollowUpReview {
  id: string;
  note: string;
  status?: FollowUpStatus;
  createdAt: string;
  createdByName: string;
}

export interface FollowUpTask {
  id: string;
  description: string;
  deadline: string;
  reviews: FollowUpReview[];
  createdAt: string;
  createdByName: string;
  currentStatus: FollowUpStatus;
}

function displayNameFromUser(user: AuthUser | null | undefined): string {
  const n = user?.name?.trim();
  if (n) return n;
  const u = user?.userName?.trim();
  if (u) return u;
  const e = user?.email?.trim();
  if (e) return e;
  return 'Unknown user';
}

export function newFollowUpId(): string {
  return crypto.randomUUID();
}

function formatDeadline(iso: string): string {
  if (!iso) return '—';
  const ymd = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return iso;
  try {
    return new Date(ymd + 'T12:00:00').toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return ymd;
  }
}

export function formatFollowUpDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function statusNumberToLabel(n: number): FollowUpStatus {
  if (n === ComplianceFollowUpStatus.InReview) return 'In review';
  if (n === ComplianceFollowUpStatus.Resolved) return 'Resolved';
  return 'Open';
}

function statusLabelToNumber(s: FollowUpStatus): number {
  if (s === 'In review') return ComplianceFollowUpStatus.InReview;
  if (s === 'Resolved') return ComplianceFollowUpStatus.Resolved;
  return ComplianceFollowUpStatus.Open;
}

function mapDtoToTask(dto: ComplianceFollowUpDto): FollowUpTask {
  const reviews: FollowUpReview[] = (dto.activities ?? []).map((a) => ({
    id: a.id,
    note: a.note,
    status: a.type === ComplianceActivityType.TakeAction && a.newStatus != null
      ? statusNumberToLabel(a.newStatus)
      : undefined,
    createdAt: a.creationTime,
    createdByName: a.userName || dto.creatorName || 'Unknown',
  }));

  return {
    id: dto.id,
    description: dto.description,
    deadline: dto.deadline,
    reviews,
    createdAt: dto.creationTime,
    createdByName: dto.creatorName || (dto.activities?.[0]?.userName) || 'Unknown',
    currentStatus: statusNumberToLabel(dto.currentStatus),
  };
}

export function FollowUpActivityList({ task }: { task: FollowUpTask }) {
  return (
    <ol className="space-y-3 print:space-y-2" aria-label="Follow-up activity">
      <li className="flex gap-2.5 print:gap-2">
        <span
          className="mt-1.5 size-2 shrink-0 rounded-full bg-sky-500 ring-2 ring-white print:mt-1 print:size-1.5"
          aria-hidden
        />
        <div className="min-w-0 flex-1 text-xs text-slate-700 print:text-[7px]">
          <p className="font-semibold text-slate-900">Follow-up created</p>
          <p className="mt-0.5 text-[11px] text-slate-600 print:text-[7px]">
            <span className="font-medium text-slate-700">{task.createdByName}</span>
            <span className="text-slate-400"> · </span>
            {formatFollowUpDateTime(task.createdAt)}
          </p>
        </div>
      </li>
      {task.reviews.map((r) => (
        <li key={r.id} className="flex gap-2.5 print:gap-2">
          <span
            className={`mt-1.5 size-2 shrink-0 rounded-full ring-2 ring-white print:mt-1 print:size-1.5 ${
              r.status != null ? 'bg-indigo-500' : 'bg-slate-400'
            }`}
            aria-hidden
          />
          <div className="min-w-0 flex-1 text-xs text-slate-700 print:text-[7px]">
            <p className="font-semibold text-slate-900">
              {r.status != null ? (
                <>
                  Review / status update
                  <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 print:text-[6px]">
                    {r.status}
                  </span>
                </>
              ) : (
                'Review (note only)'
              )}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-600 print:text-[7px]">
              <span className="font-medium text-slate-700">{r.createdByName}</span>
              <span className="text-slate-400"> · </span>
              {formatFollowUpDateTime(r.createdAt)}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-slate-800 print:text-[7px]">{r.note}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export type FollowUpsEditorHandle = {
  openAdd: () => void;
};

type ActionMode = 'takeAction' | 'addReview';

export type FollowUpsEditorProps = {
  scopeKey: string;
  showInlineAddButton?: boolean;
  contextHint?: ReactNode;
  complianceCreateScope?: ComplianceCreateScope;
};

export const FollowUpsEditor = forwardRef<FollowUpsEditorHandle, FollowUpsEditorProps>(function FollowUpsEditor(
  { scopeKey, showInlineAddButton = false, contextHint, complianceCreateScope },
  ref
) {
  const { user } = useAuth();
  const actorName = useMemo(() => displayNameFromUser(user), [user]);

  const [tasks, setTasks] = useState<FollowUpTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addDescription, setAddDescription] = useState('');
  const [addDeadline, setAddDeadline] = useState('');
  const [actionTask, setActionTask] = useState<FollowUpTask | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>('takeAction');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewStatus, setReviewStatus] = useState<FollowUpStatus>('Open');
  const [addSaving, setAddSaving] = useState(false);
  const [actionSaving, setActionSaving] = useState(false);

  const fetchList = useCallback(async () => {
    if (!complianceCreateScope) return;
    setLoading(true);
    try {
      const params = complianceCreateScope.kind === 'school'
        ? { schoolId: complianceCreateScope.schoolId, source: ComplianceFollowUpSource.SchoolProfile, year: complianceCreateScope.reportingYear }
        : { schoolId: complianceCreateScope.schoolId, riskTriageQueueItemId: complianceCreateScope.riskTriageQueueItemId, source: ComplianceFollowUpSource.RiskTriage, year: complianceCreateScope.reportingYear };
      const res = await complianceApi.getList({ ...params, maxResultCount: 100 });
      setTasks((res?.items ?? []).map(mapDtoToTask));
    } catch (e) {
      console.error('[FollowUpsEditor] fetchList failed:', e);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [complianceCreateScope]);

  useEffect(() => {
    setTasks([]);
    setAddOpen(false);
    setActionTask(null);
    void fetchList();
  }, [scopeKey, fetchList]);

  const openAdd = useCallback(() => {
    setAddDescription('');
    setAddDeadline('');
    setAddOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ openAdd }), [openAdd]);

  const submitAdd = useCallback(async () => {
    const d = addDescription.trim();
    if (!d) { message.error('Description is required.'); return; }
    const dl = addDeadline.trim();
    if (!dl) { message.error('Deadline is required.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dl)) { message.error('Use a valid deadline date.'); return; }
    setAddSaving(true);
    try {
      if (complianceCreateScope) {
        await complianceApi.createFromScope(complianceCreateScope, d, dl);
        message.success('Compliance follow-up saved.');
      } else {
        const createdAt = new Date().toISOString();
        setTasks((prev) => [
          ...prev,
          { id: newFollowUpId(), description: d, deadline: dl, reviews: [], createdAt, createdByName: actorName, currentStatus: 'Open' },
        ]);
        message.success('Follow-up added (this session only).');
      }
      setAddOpen(false);
      if (complianceCreateScope) await fetchList();
    } catch (e) {
      const msg = e instanceof ApiError ? ApiError.getUserMessage(e) : e instanceof Error ? e.message : 'Could not save follow-up.';
      message.error(msg);
    } finally {
      setAddSaving(false);
    }
  }, [addDescription, addDeadline, actorName, complianceCreateScope, fetchList]);

  const openAction = useCallback((t: FollowUpTask, mode: ActionMode) => {
    setActionTask(t);
    setActionMode(mode);
    setReviewNote('');
    setReviewStatus(t.currentStatus);
  }, []);

  const submitReview = useCallback(async () => {
    if (!actionTask) return;
    const note = reviewNote.trim();
    if (!note) { message.error('Review note is required.'); return; }
    setActionSaving(true);
    try {
      if (actionMode === 'addReview') {
        await complianceApi.addReview(actionTask.id, { note });
        message.success('Review saved.');
      } else {
        await complianceApi.takeAction(actionTask.id, {
          newStatus: statusLabelToNumber(reviewStatus),
          note,
        });
        message.success('Status updated and review saved.');
      }
      setActionTask(null);
      await fetchList();
    } catch (e) {
      const msg = e instanceof ApiError ? ApiError.getUserMessage(e) : e instanceof Error ? e.message : 'Could not save.';
      message.error(msg);
    } finally {
      setActionSaving(false);
    }
  }, [actionTask, actionMode, reviewNote, reviewStatus, fetchList]);

  return (
    <>
      {showInlineAddButton ? (
        <div className="mb-3 flex flex-wrap justify-end print:hidden">
          <Button
            type="button"
            variant="primary"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
            onClick={openAdd}
          >
            {ADD_COMPLIANCE_ISSUE_LABEL}
          </Button>
        </div>
      ) : null}
      {contextHint ? <div className="mb-3 text-xs text-slate-600 print:mb-2 print:text-[8px]">{contextHint}</div> : null}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="size-6 animate-spin text-indigo-600" />
        </div>
      ) : tasks.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-600 print:px-2 print:py-3 print:text-[8px]">
          No follow-ups yet. Use <strong>{ADD_COMPLIANCE_ISSUE_LABEL}</strong> for description + deadline
          {complianceCreateScope ? ' (saved to the server)' : ''}. Use <strong>Take action</strong> to change status and
          record a review; use <strong>Add review</strong> for a note that does not change status.
        </p>
      ) : (
        <ul className="space-y-3 print:space-y-2">
          {tasks.map((t) => (
            <li
              key={t.id}
              className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-100 print:rounded-md print:p-2 print:shadow-none"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1 space-y-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold leading-snug text-slate-900 print:text-[9px]">{t.description}</p>
                    <p className="text-xs text-slate-600 print:text-[8px]">
                      <span className="font-semibold text-slate-700">Deadline:</span> {formatDeadline(t.deadline)}
                    </p>
                    <p className="text-xs text-slate-600 print:text-[8px]">
                      <span className="font-semibold text-slate-700">Current status:</span>{' '}
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-800">
                        {t.currentStatus}
                      </span>
                    </p>
                  </div>
                  <div>
                    <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 print:text-[7px]">
                      Activity
                    </h4>
                    <FollowUpActivityList task={t} />
                  </div>
                </div>
                <div className="shrink-0 print:mt-1">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="px-3 py-1.5 text-xs print:text-[7px]"
                      onClick={() => openAction(t, 'takeAction')}
                    >
                      Take action
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="px-3 py-1.5 text-xs print:text-[7px]"
                      onClick={() => openAction(t, 'addReview')}
                    >
                      Add review
                    </Button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        title={ADD_COMPLIANCE_ISSUE_LABEL}
        open={addOpen}
        onCancel={() => {
          setAddOpen(false);
          setAddSaving(false);
        }}
        onOk={() => submitAdd()}
        okText="Save"
        confirmLoading={addSaving}
        destroyOnClose
        maskClosable
      >
        <div className="flex flex-col gap-3 pt-1">
          <div>
            <label htmlFor="followup-desc" className="mb-1 block text-xs font-semibold text-slate-700">
              Description <span className="text-red-600">*</span>
            </label>
            <Input.TextArea
              id="followup-desc"
              rows={4}
              value={addDescription}
              onChange={(e) => setAddDescription(e.target.value)}
              placeholder="What should be done or verified?"
            />
          </div>
          <div>
            <label htmlFor="followup-deadline" className="mb-1 block text-xs font-semibold text-slate-700">
              Deadline <span className="text-red-600">*</span>
            </label>
            <input
              id="followup-deadline"
              type="date"
              className="w-full max-w-xs rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={addDeadline}
              onChange={(e) => setAddDeadline(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <span className="inline-flex items-center gap-2">
            <ClipboardList className="size-4 text-slate-600" aria-hidden />
            {actionMode === 'addReview' ? 'Add review (note only)' : 'Take action — status update'}
          </span>
        }
        open={Boolean(actionTask)}
        onCancel={() => setActionTask(null)}
        onOk={submitReview}
        okText={actionMode === 'addReview' ? 'Save review' : 'Save status & review'}
        confirmLoading={actionSaving}
        destroyOnClose
        maskClosable
      >
        {actionTask ? (
          <div className="flex flex-col gap-3 pt-1">
            <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <p className="font-semibold text-slate-900">{actionTask.description}</p>
              <p className="mt-1 text-slate-600">Deadline: {formatDeadline(actionTask.deadline)}</p>
              <p className="mt-1 text-slate-600">
                Current status: <span className="font-semibold text-slate-900">{actionTask.currentStatus}</span>
              </p>
            </div>
            {actionMode === 'addReview' ? (
              <p className="rounded-md border border-sky-100 bg-sky-50/80 px-3 py-2 text-xs text-sky-950">
                This adds a note to the activity log only. It does <strong>not</strong> change the follow-up status.
                Use <strong>Take action</strong> to update status.
              </p>
            ) : null}
            {actionTask.reviews.length > 0 ? (
              <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white px-3 py-2">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Activity so far</p>
                <FollowUpActivityList task={actionTask} />
              </div>
            ) : null}
            <div>
              <label htmlFor="review-note" className="mb-1 block text-xs font-semibold text-slate-700">
                Review note <span className="text-red-600">*</span>
              </label>
              <Input.TextArea
                id="review-note"
                rows={4}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Document review outcome or next steps…"
              />
            </div>
            {actionMode === 'takeAction' ? (
              <div>
                <label htmlFor="review-status" className="mb-1 block text-xs font-semibold text-slate-700">
                  New status <span className="text-red-600">*</span>
                </label>
                <Select
                  id="review-status"
                  className="w-full"
                  value={reviewStatus}
                  onChange={(v) => setReviewStatus(v as FollowUpStatus)}
                  options={(
                    [
                      { value: 'Open', label: 'Open' },
                      { value: 'In review', label: 'In review' },
                      { value: 'Resolved', label: 'Resolved' },
                    ] as const
                  ).map((o) => ({ value: o.value, label: o.label }))}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
});

FollowUpsEditor.displayName = 'FollowUpsEditor';
